import React, { useState, useEffect } from "react";
import SEO from "../seo";

const DiscountCodesInXPage = () => {
  const [activeSection, setActiveSection] = useState("why-use-discount-codes");

  useEffect(() => {
    const handleScroll = () => {
      const sections = [
        "why-use-discount-codes",
        "how-discount-codes-work",
        "how-to-create",
        "best-practices",
        "important-considerations",
      ];

      const scrollPosition = window.scrollY + 100;

      for (const sectionId of sections) {
        const element = document.getElementById(sectionId);
        if (element) {
          const offsetTop = element.offsetTop;
          const offsetHeight = element.offsetHeight;

          if (
            scrollPosition >= offsetTop &&
            scrollPosition < offsetTop + offsetHeight
          ) {
            setActiveSection(sectionId);
            break;
          }
        }
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <div className="flex flex-col lg:flex-row gap-6">
      <SEO
        title="Auto Currency Conversion | xPage Help Center"
        description="Auto Currency Conversion automatically detects your customer’s location and displays prices in their local currency."
        keywords="xPage, auto currency conversion, multi currency, exchange rates"
        canonical="https://help.xpage.ai/help/business-expansion/Auto%20Currency%20Conversion"
        type="article"
      />

      <div className="flex-1 prose max-w-none">
        <header id="why-use-discount-codes" className="mb-6 scroll-mt-20">
          <h1 className="text-systemTitle text-2xl font-semibold mb-3">
            Auto Currency Conversion
          </h1>
          <p className="text-sm text-systemLabel">
            Auto Currency Conversion automatically detects your customer’s
            location and displays prices in their local currency. This helps
            improve clarity, trust, and conversion rates by showing familiar
            pricing at checkout.
          </p>
        </header>

        <section id="why-use-discount-codes" className="mb-6 scroll-mt-20">
          <h2 className="text-sm font-semibold text-systemTitle mb-1">
            What is Auto Currency Conversion?
          </h2>

          <div className="bg-systemBackground border border-systemBorder rounded-lg p-4 mt-2">
            <p className="text-sm text-systemLabel">
              Auto Currency Conversion automatically detects your customer’s
              location and displays prices in their local currency. This helps
              improve clarity, trust, and conversion rates by showing familiar
              pricing at checkout.
            </p>
          </div>
        </section>

        <section id="how-discount-codes-work" className="mb-6 scroll-mt-20">
          <h2 className="text-sm font-semibold text-systemTitle mb-1">
            How It Works
          </h2>

          <div className="bg-systemBackground border border-systemBorder rounded-lg p-4 mt-2">
            <ul className="list-disc list-inside space-y-1 text-sm text-systemLabel">
              <li>
                XPage detects the visitor’s country using their IP address.
              </li>
              <li>The system matches the country to its local currency.</li>
              <li>
                Prices are automatically converted from your store’s base
                currency using live exchange rates.
              </li>
              <li>
                Customers see localized pricing across the landing page and
                checkout.
              </li>
            </ul>
          </div>
        </section>

        <section id="how-to-create" className="mb-6 scroll-mt-20">
          <h2 className="text-sm font-semibold text-systemTitle mb-1">
            How to Enable It
          </h2>

          <div className="bg-systemBackground border border-systemBorder rounded-lg p-4 mt-2">
            <ol className="list-decimal list-inside space-y-2 text-sm text-systemLabel ml-4">
              <li>
                <span className="font-medium">Go to <strong>Settings</strong> → <strong>Currency</strong></span>
              </li>
              <li>
                <span className="font-medium">
                  Activate <strong>Auto Convert</strong> Based on <strong>Customer Location</strong>
                </span>
              </li>
              <li>
                <span className="font-medium">Save changes</span>
              </li>
            </ol>

            <div className="bg-system200 border-l-4 border-system400 px-4 py-3 rounded-r-md mt-4">
              <p className="text-sm text-systemLabel">
                Once enabled, currency switching happens automatically — no
                manual selection required.
              </p>
            </div>
          </div>
        </section>

        <section id="best-practices" className="mb-6 scroll-mt-20">
          <h2 className="text-sm font-semibold text-systemTitle mb-1">
            Important Notes
          </h2>

          <div className="bg-systemBackground border border-systemBorder rounded-lg p-4 mt-2">
            <ul className="list-disc list-inside space-y-1 text-sm text-systemLabel">
              <li>Your base currency remains unchanged.</li>
              <li>Exchange rates are updated automatically.</li>
              <li>You can still allow manual currency selection if needed.</li>
              <li>
                Payment processing is completed in the customer’s
                selected/displayed currency (depending on your payment provider
                setup).
              </li>
            </ul>
          </div>
        </section>

        <section id="important-considerations" className="scroll-mt-20">
          <h2 className="text-sm font-semibold text-systemTitle mb-1">
            When Should You Use It?
          </h2>

          <div className="bg-systemBackground border border-systemBorder rounded-lg p-4 mt-2">
            <h3 className="text-sm font-semibold text-systemTitle mb-2">
              Enable this feature if:
            </h3>
            <ul className="list-disc list-inside space-y-1 text-sm text-systemLabel mb-4">
              <li>You sell internationally</li>
              <li>You run paid ads targeting multiple countries</li>
              <li>You want to reduce checkout friction</li>
            </ul>

            <h3 className="text-sm font-semibold text-systemTitle mb-2">
              Avoid enabling it if:
            </h3>
            <ul className="list-disc list-inside space-y-1 text-sm text-systemLabel">
              <li>You only sell in one country</li>
              <li>
                Your payment provider does not support multi-currency
                settlements
              </li>
            </ul>
          </div>
        </section>
      </div>

      <div className="lg:w-64 flex-shrink-0 lg:block hidden">
        <div className="sticky top-6 bg-systemBackground border border-systemBorder rounded-lg p-4">
          <h3 className="text-systemTitle text-sm font-semibold mb-3">
            On this page
          </h3>
          <ul className="text-systemLabel text-sm space-y-2">
            {[
              {
                id: "why-use-discount-codes",
                label: "What is Auto Currency Conversion?",
              },
              { id: "how-discount-codes-work", label: "How It Works" },
              { id: "how-to-create", label: "How to Enable It" },
              { id: "best-practices", label: "Important Notes" },
              {
                id: "important-considerations",
                label: "When Should You Use It?",
              },
            ].map((item) => (
              <li key={item.id}>
                <a
                  href={`#${item.id}`}
                  className={`block py-1 transition-colors border-l-2 pl-3 ${
                    activeSection === item.id
                      ? "text-deepPurple font-semibold border-deepPurple"
                      : "border-transparent hover:text-deepPurple"
                  }`}
                >
                  {item.label}
                </a>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
};

export default DiscountCodesInXPage;
